// Image types the API accepts for photos.
const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
];

// Longest side of an uploaded profile photo. Avatars are small, so this is
// plenty and keeps the upload quick.
const MAX_PROFILE_IMAGE_SIDE = 1024;

// The file isn't an image we can read or the API can store.
export class UnsupportedImageError extends Error {
  constructor() {
    super('Unsupported image');
    this.name = 'UnsupportedImageError';
  }
}

// Turns a picked or downloaded image into a JPEG of at most 1024px, like the
// Swift apps send. Redrawing it also drops the file's metadata, such as where
// a phone photo was taken. If the browser can't read the image (HEIC outside
// Safari), it is sent as it is when the API accepts that type.
export const toProfileJpeg = async (image: Blob): Promise<Blob> => {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(image);
  } catch {
    if (ACCEPTED_IMAGE_TYPES.includes(image.type)) {
      return image;
    }
    throw new UnsupportedImageError();
  }

  const scale = Math.min(
    1,
    MAX_PROFILE_IMAGE_SIDE / Math.max(bitmap.width, bitmap.height),
  );
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);

  const context = canvas.getContext('2d');
  if (!context) {
    throw new UnsupportedImageError();
  }
  // JPEG has no transparency, so give see-through PNGs a white background.
  context.fillStyle = '#FFFFFF';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const jpeg = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.9),
  );
  if (!jpeg) {
    throw new UnsupportedImageError();
  }
  return jpeg;
};

// A file name with the right extension for the image's type, e.g.
// `profile.jpg`.
export const imageFilename = (image: Blob, name = 'profile'): string => {
  const subtype = image.type.split('/')[1] ?? 'jpeg';
  return `${name}.${subtype === 'jpeg' ? 'jpg' : subtype}`;
};
